import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import { productApi, saleApi, shiftApi, categoryApi, skuApi } from '../api';
import ProductGrid from '../components/pos/ProductGrid';
import ProductSelectorDialog from '../components/pos/ProductSelectorDialog';
import ShoppingCart from '../components/pos/ShoppingCart';
import CheckoutConfirmDialog from '../components/pos/CheckoutConfirmDialog';
import KhqrPaymentDialog from '../components/pos/KhqrPaymentDialog';
import PendingOrdersDialog from '../components/pos/PendingOrdersDialog';
import ProductSkuDialog from '../components/pos/ProductSkuDialog';

// ⚠️ 需要在 ../api 里加一个 productApi.getAvailableBatches(productId, skuId)，
// 对应后端新增的 GET /api/products/:id/batches?sku_id=xxx 这个接口。
// api client那个文件没看过具体写法，下面按其他现有调用（productApi.getList等）
// 同样的风格假设了一个调用方式，如果实际项目里参数/返回结构不同，这里要跟着调整：
// const res = await productApi.getAvailableBatches(productId, skuId);
// 期望 res.data 是 [{ id, batch_no, quantity, expiry_date }]，按到期日期升序排好的

// 购物车唯一 key：同商品的不同售卖方式/价格/规格算不同行
// sku_id 参与 key，才能区分「红色L」和「蓝色M」
const getItemKey = (item) =>
  `${item.product_id}-${item.sell_type || 'unit'}-${item.price_type || 'retail'}-${item.sku_id || 0}`;

// 按到期日期从早到晚（batches已经是排好序的），给定需要的数量，算出默认的FIFO分配方案，
// 和后端 autoDeductFIFO 的分配逻辑保持一致——这只是客户端的"预览"，真正下单时
// 后端还是会用同一套逻辑校验；这里算出来主要是让购物车能展示"计划扣哪些批次"，
// 并且作为"手动调整"的默认起点
function computeDefaultAllocation(batches, neededQty) {
  const allocations = [];
  let remaining = neededQty;
  for (const batch of batches) {
    if (remaining <= 0) break;
    const take = Math.min(batch.quantity, remaining);
    if (take > 0) {
      allocations.push({ batch_id: batch.id, quantity: take, expiry_date: batch.expiry_date });
      remaining -= take;
    }
  }
  return allocations;
}

// 加购之后，如果这个商品有多个批次，查一下批次列表、算好默认分配方案，
// 挂在这个cart item身上（availableBatches / batchAllocations），供购物车展示提示+手动调整
async function attachBatchInfo(item) {
  try {
    const res = await productApi.getAvailableBatches(item.product_id, item.sku_id || undefined);
    if (res.success && res.data && res.data.length > 1) {
      return {
        ...item,
        availableBatches: res.data,
        batchAllocations: computeDefaultAllocation(res.data, item.quantity),
      };
    }
  } catch (err) {
    // 查批次失败不影响正常加购，静默降级——这个商品就当只有一个批次处理，
    // 后端自己的autoDeductFIFO兜底
    console.warn('查询批次信息失败:', err);
  }
  return item;
}

const POSPage = () => {
  const { language, currentUser } = useApp();
  const t = translations[language];
  const { onShowReceipt } = useOutletContext() || {};

  const [products, setProducts]               = useState([]);
  const [categoryTree, setCategoryTree]       = useState([]);
  const [cartItems, setCartItems]             = useState([]);
  const [currentShift, setCurrentShift]       = useState(null);
  const [paymentMethod, setPaymentMethod]     = useState('cash');
  const [searchKeyword, setSearchKeyword]     = useState('');
  const [selectedParent, setSelectedParent]   = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showKhqrDialog, setShowKhqrDialog]       = useState(false);
  const [showPendingDialog, setShowPendingDialog] = useState(false);
  const [pendingOrderNo, setPendingOrderNo]   = useState(null);
  const [pendingCount, setPendingCount]       = useState(0);

  // 两个弹窗各自的目标商品
  const [skuDialog,      setSkuDialog]      = useState(null); // 有规格 → ProductSkuDialog
  const [selectorDialog, setSelectorDialog] = useState(null); // 无规格 → ProductSelectorDialog
  const [checkingId,     setCheckingId]     = useState(null); // 正在查规格的商品 id，防重复点击

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadCurrentShift();
    loadPendingCount();
  }, []);

  const loadProducts = async () => {
    try {
      const res = await productApi.getList({ status: 'active', pos_mode: '1' });
      if (res.success) setProducts(res.data || []);
    } catch (err) {
      console.error('加载商品失败:', err);
      alert('加载商品失败: ' + err.message);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await categoryApi.getList();
      if (res.success) setCategoryTree(res.data || []);
    } catch (err) { console.error('加载分类失败:', err); }
  };

  const loadCurrentShift = async () => {
    try {
      const res = await shiftApi.getCurrent();
      if (res.success && res.data) setCurrentShift(res.data);
    } catch (err) { console.error('加载班次失败:', err); }
  };

  const loadPendingCount = async () => {
    try {
      const res = await saleApi.getPending();
      if (res.success) setPendingCount(res.data?.length || 0);
    } catch {}
  };

  // ★ 唯一入口：点商品后先判断有无规格，再决定弹哪个窗
  // 优先看后端返回的 has_specs 字段；没有该字段时兜底调接口查一次
  const handleProductClick = async (product) => {
    if (checkingId === product.id) return;

    // 1) 商品列表已带 has_specs，直接分流，免一次请求
    if (product.has_specs === true) {
      setSkuDialog(product);
      return;
    }
    if (product.has_specs === false) {
      setSelectorDialog(product);
      return;
    }

    // 2) 兜底：字段缺失时查接口
    setCheckingId(product.id);
    try {
      const res = await skuApi.getProductSpecs(product.id);
      if (res.success && res.data.has_specs) {
        setSkuDialog(product);
      } else {
        setSelectorDialog(product);
      }
    } catch (err) {
      console.error('查询规格失败，降级为普通选择:', err);
      setSelectorDialog(product);
    } finally {
      setCheckingId(null);
    }
  };

  // 无规格商品：ProductSelectorDialog 已构建好完整 cart item
  const handleAddToCart = async (item) => {
    const key = getItemKey(item);
    const existing = cartItems.find(i => getItemKey(i) === key);

    if (existing) {
      // 已经在购物车里了，只是加数量——批次分配方案需要跟着重新算一次
      // （数量变了，默认分配也要跟着变；如果店员之前手动调整过，这里会被新的默认值覆盖，
      // 是可接受的取舍：不然要精确处理"部分手动+新增数量怎么分"会复杂很多）
      const newQty = existing.quantity + item.quantity;
      let updated = { ...existing, quantity: newQty };
      if (existing.availableBatches && existing.availableBatches.length > 1) {
        updated.batchAllocations = computeDefaultAllocation(existing.availableBatches, newQty);
      }
      setCartItems(prev => prev.map(i => getItemKey(i) === key ? updated : i));
      return;
    }

    const itemWithBatch = await attachBatchInfo(item);
    setCartItems(prev => [...prev, itemWithBatch]);
  };

  // 有规格商品：把 SKU 弹窗的返回整理成标准 cart item 再入车
  const handleAddSkuToCart = (productWithSku) => {
    const item = {
      product_id:      productWithSku.id,
      product_name:    productWithSku.name,
      product_name_en: productWithSku.name_en,
      product_name_km: productWithSku.name_km,
      sell_type:       'unit',
      price_type:      'retail',
      unit_price:      parseFloat(productWithSku.sale_price || 0),
      quantity:        productWithSku.quantity || 1,
      barcode:         productWithSku.barcode,
      // SKU 专属
      sku_id:          productWithSku.sku_id,
      sku_label:       productWithSku.sku_label,
      sku_image:       productWithSku.sku_image,
    };
    handleAddToCart(item);
    setSkuDialog(null);
  };

  const handleUpdateQuantity = (key, newQuantity) => {
    if (newQuantity <= 0) { handleRemoveFromCart(key); return; }
    setCartItems(prev => prev.map(i => {
      if (getItemKey(i) !== key) return i;
      const updated = { ...i, quantity: newQuantity };
      // 数量变了，如果这个商品有多批次，默认分配方案要跟着重新算
      // （店员之前如果手动调整过，这里会被覆盖成新的默认值——同上，可接受的取舍）
      if (i.availableBatches && i.availableBatches.length > 1) {
        updated.batchAllocations = computeDefaultAllocation(i.availableBatches, newQuantity);
      }
      return updated;
    }));
  };

  const handleRemoveFromCart = (key) => {
    setCartItems(prev => prev.filter(i => getItemKey(i) !== key));
  };

  // 店员在购物车里手动调整了某个商品的批次分配——直接覆盖掉默认的那份
  const handleUpdateBatchAllocation = (key, allocations) => {
    setCartItems(prev => prev.map(i =>
      getItemKey(i) === key ? { ...i, batchAllocations: allocations } : i
    ));
  };

  // 构建订单数据（带 sku_id，后端据此扣 product_skus.stock；
  // 多批次商品带 batch_allocations，后端按这个精确分配扣减，不再自动FIFO）
  const buildOrderData = (method) => ({
    shift_id: parseInt(currentShift.id),
    payment_method: method,
    items: cartItems.map(item => ({
      product_id:   parseInt(item.product_id),
      product_name: item.product_name,
      quantity:     parseInt(item.quantity),
      unit_price:   parseFloat(item.unit_price),
      subtotal:     parseFloat((item.unit_price * item.quantity).toFixed(2)),
      sell_type:    item.sell_type  || 'unit',
      price_type:   item.price_type || 'retail',
      sku_id:       item.sku_id || null,
      sku_label:    item.sku_label || null,
      // 只有真的涉及多个批次时才传，单批次商品不传，让后端走默认的自动FIFO
      // （单批次情况下手动传和自动算结果一样，没必要额外传这份数据）
      batch_allocations: (item.availableBatches && item.availableBatches.length > 1 && item.batchAllocations)
        ? item.batchAllocations.map(a => ({ batch_id: a.batch_id, quantity: a.quantity }))
        : undefined,
    })),
  });

  const handleCheckoutClick = () => {
    if (cartItems.length === 0) { alert(t.cartIsEmpty || '购物车是空的！'); return; }
    if (!currentShift) { alert(t.pleaseStartShift || '请先开始班次！'); return; }
    if (paymentMethod === 'mobile') {
      handleKhqrCheckout();
    } else {
      setShowConfirmDialog(true);
    }
  };

  const handleKhqrCheckout = async () => {
    try {
      const res = await saleApi.create(buildOrderData('mobile'));
      if (res.success) {
        setPendingOrderNo(res.data.order_no);
        setShowKhqrDialog(true);
      } else {
        alert(res.message || '创建订单失败');
      }
    } catch (err) {
      console.error('创建KHQR订单失败:', err);
      alert(err.response?.data?.message || err.message || '创建订单失败，请重试');
    }
  };

  const handleConfirmPayment = async () => {
    setShowConfirmDialog(false);
    try {
      const res = await saleApi.create(buildOrderData(paymentMethod));
      if (res.success) {
        if (onShowReceipt) onShowReceipt(res.data);
        setCartItems([]);
        await loadProducts();
        await loadCurrentShift();
      } else {
        alert(res.message || '订单创建失败');
      }
    } catch (err) {
      console.error('创建订单失败:', err);
      alert(err.response?.data?.message || err.message || '创建订单失败，请重试');
    }
  };

  const handleKhqrSuccess = async (wsData) => {
    setShowKhqrDialog(false);
    setPendingOrderNo(null);
    setCartItems([]);
    await loadProducts();
    await loadCurrentShift();
    if (onShowReceipt && wsData) {
      onShowReceipt({
        order_no:       wsData.order_no,
        total_amount:   wsData.total_amount,
        payment_method: 'mobile',
      });
    }
  };

  const handleKhqrCancel = async () => {
    const confirmed = window.confirm(
      '关闭前请确认客户未付款。\n\n如果客户已扫码付款，请勿关闭，等待系统自动确认。\n\n确定关闭吗？'
    );
    if (!confirmed) return;

    if (pendingOrderNo) {
      try { await saleApi.cancelSale(pendingOrderNo); } catch {}
    }
    setShowKhqrDialog(false);
    setPendingOrderNo(null);
    await loadProducts();
  };

  const handleKhqrRetry = async () => {
    if (pendingOrderNo) {
      try { await saleApi.cancelSale(pendingOrderNo); } catch {}
      setPendingOrderNo(null);
    }
    await handleKhqrCheckout();
  };

  const handleHold = async () => {
    if (cartItems.length === 0) { alert('购物车是空的'); return; }
    if (!currentShift) { alert('请先开始班次'); return; }
    try {
      const res = await saleApi.create(buildOrderData('hold'));
      if (res.success) {
        alert('挂单成功');
        setCartItems([]);
        await loadPendingCount();
        await loadProducts();
      } else {
        alert(res.message || '挂单失败');
      }
    } catch (err) {
      alert(err.response?.data?.message || '挂单失败');
    }
  };

  const handleRestorePending = (order) => {
    if (cartItems.length > 0) {
      if (!window.confirm('当前购物车有商品，取出挂单会替换当前购物车，确定吗？')) return;
    }
    const restoredItems = order.items.map(item => ({
      product_id:   item.product_id,
      product_name: item.product_name,
      unit_price:   parseFloat(item.unit_price),
      quantity:     item.quantity,
      sell_type:    item.sell_type  || 'unit',
      price_type:   item.price_type || 'retail',
      subtotal:     parseFloat(item.subtotal),
      sku_id:       item.sku_id || null,
      sku_label:    item.sku_label || null,
    }));
    setCartItems(restoredItems);
    saleApi.cancelSale(order.order_no).catch(() => {});
    loadPendingCount();
  };

  const handleSelectParent = (parentId) => {
    setSelectedParent(selectedParent === parentId ? null : parentId);
    setSelectedCategory(null);
  };

  const handleSelectChild = (childId) => {
    setSelectedCategory(childId === selectedCategory ? null : childId);
  };

  const getChildIds = (parentId) => {
    const parent = categoryTree.find(p => p.id === parentId);
    return parent?.children?.map(c => c.id) || [];
  };

  const getCategoryName = (cat) => {
    if (!cat) return '';
    if (language === 'en') return cat.name_en || cat.name;
    if (language === 'km') return cat.name_km || cat.name;
    return cat.name;
  };

  const filteredProducts = products.filter(product => {
    if (selectedCategory !== null) {
      if (product.category_id !== selectedCategory) return false;
    } else if (selectedParent !== null) {
      const childIds = getChildIds(selectedParent);
      if (childIds.length > 0 && !childIds.includes(product.category_id)) return false;
    }
    if (!searchKeyword) return true;
    const kw = searchKeyword.toLowerCase();
    return (
      product.name?.toLowerCase().includes(kw) ||
      product.name_en?.toLowerCase().includes(kw) ||
      product.name_km?.toLowerCase().includes(kw) ||
      product.barcode?.includes(kw)
    );
  });

  const currentChildren = selectedParent
    ? (categoryTree.find(p => p.id === selectedParent)?.children || [])
    : [];

  return (
    <div className="h-full flex flex-col lg:flex-row gap-6">
      <div className="flex-1 lg:w-2/3 min-h-0">
        <div className="bg-white rounded-xl shadow-lg p-6 h-full flex flex-col">
          <div className="flex-shrink-0 space-y-4 mb-4">
            <input type="text"
              placeholder={t.searchPlaceholder || '搜索商品名称或条码...'}
              value={searchKeyword}
              onChange={e => setSearchKeyword(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" />

            <div className="overflow-x-auto">
              <div className="flex gap-2 pb-1">
                <button onClick={() => { setSelectedParent(null); setSelectedCategory(null); }}
                  className={`px-3 py-1 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                    selectedParent === null ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}>
                  {language === 'en' ? 'All' : language === 'km' ? 'ទាំងអស់' : '全部'}
                </button>
                {categoryTree.map(parent => (
                  <button key={parent.id} onClick={() => handleSelectParent(parent.id)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                      selectedParent === parent.id ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}>
                    {getCategoryName(parent)}
                  </button>
                ))}
              </div>
            </div>

            {selectedParent !== null && currentChildren.length > 0 && (
              <div className="overflow-x-auto">
                <div className="flex gap-2 pb-1">
                  <button onClick={() => setSelectedCategory(null)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                      selectedCategory === null ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}>
                    全部子分类
                  </button>
                  {currentChildren.map(child => (
                    <button key={child.id} onClick={() => handleSelectChild(child.id)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                        selectedCategory === child.id ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                      }`}>
                      {getCategoryName(child)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!currentShift && (
              <div className="px-3 py-1.5 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-700">⚠️ 请先在"班次管理"页面开始班次</p>
              </div>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">
            <ProductGrid products={filteredProducts} onProductClick={handleProductClick} />
          </div>
        </div>
      </div>

      <div className="lg:w-1/3 min-h-0">
        <ShoppingCart
          cartItems={cartItems}
          getItemKey={getItemKey}
          onUpdateQuantity={handleUpdateQuantity}
          onRemoveItem={handleRemoveFromCart}
          onUpdateBatchAllocation={handleUpdateBatchAllocation}
          onCheckout={handleCheckoutClick}
          onHold={handleHold}
          onOpenPending={() => setShowPendingDialog(true)}
          pendingCount={pendingCount}
          paymentMethod={paymentMethod}
          onPaymentMethodChange={setPaymentMethod}
        />
      </div>

      {/* 无规格：散装/整箱/VIP 选择 */}
      {selectorDialog && (
        <ProductSelectorDialog
          product={selectorDialog}
          onConfirm={handleAddToCart}
          onClose={() => setSelectorDialog(null)}
        />
      )}

      {/* 有规格：SKU 选择 */}
      {skuDialog && (
        <ProductSkuDialog
          product={skuDialog}
          onClose={() => setSkuDialog(null)}
          onAddToCart={handleAddSkuToCart}
        />
      )}

      {showConfirmDialog && (
        <CheckoutConfirmDialog
          cartItems={cartItems}
          paymentMethod={paymentMethod}
          onConfirm={handleConfirmPayment}
          onCancel={() => setShowConfirmDialog(false)}
        />
      )}

      {showKhqrDialog && pendingOrderNo && (
        <KhqrPaymentDialog
          amount={cartItems.reduce((sum, item) => sum + item.unit_price * item.quantity, 0).toFixed(2)}
          orderNo={pendingOrderNo}
          onSuccess={handleKhqrSuccess}
          onCancel={handleKhqrCancel}
          onRetry={handleKhqrRetry}
        />
      )}

      {showPendingDialog && (
        <PendingOrdersDialog
          onClose={() => setShowPendingDialog(false)}
          onRestore={handleRestorePending}
        />
      )}
    </div>
  );
};

export default POSPage;